import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    GoogleAuthProvider,
    EmailAuthProvider,
    signInWithPopup,
    getAdditionalUserInfo,
    deleteUser,
    reauthenticateWithPopup,
    reauthenticateWithCredential,
    sendPasswordResetEmail,
    FacebookAuthProvider,
} from 'firebase/auth';
import { getUserDefaultProfile } from '../data/userProfile';
import db from '../db/index';
import { doc, deleteDoc } from 'firebase/firestore';

const createUserProfile = userProfile =>
    db.collection('profiles').doc(userProfile.uid).set(userProfile);

const register = async({ email, password, fullName }) => {
    const auth = getAuth();
    try {
        const res = await createUserWithEmailAndPassword(auth, email, password);
        const { user } = res;
        const userProfile = getUserDefaultProfile(user.uid, fullName, email);
        await createUserProfile(userProfile);
        return userProfile;
    } catch (error) {
        return Promise.reject(error.message);
    }
};

const registerWithGoogle = async() => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    try {
        const res = await signInWithPopup(auth, provider);

        // TODO: need to handle the case where the user hasn't signed up yet but trying to log in with google

        const details = getAdditionalUserInfo(res);
        const isNewUser = details.isNewUser;

        const user = res.user;
        const userProfile = getUserDefaultProfile(user.uid, user.displayName, user.email);

        if (isNewUser) {
            await createUserProfile(userProfile);
        }

        return {
            ...user,
            isNewUser,
        };
    } catch (error) {
        return Promise.reject(error.message);
    }
};

const registerWithFacebook = async() => {
    const auth = getAuth();
    const provider = new FacebookAuthProvider();

    try {
        const res = await signInWithPopup(auth, provider);
        const user = res.user;

        const details = getAdditionalUserInfo(res);
        const isNewUser = details.isNewUser;
        const userProfile = getUserDefaultProfile(user.uid, user.displayName, user.email);

        if (isNewUser) {
            await createUserProfile(userProfile);
        }

        return {
            ...userProfile,
            isNewUser,
        };
    } catch (error) {
        return Promise.reject(error.message);
    }
};

const resetPassword = async(email) => {
    const auth = getAuth();
    try {
        await sendPasswordResetEmail(auth, email);
    } catch (error) {
        return Promise.reject(error.message);
    }
};

const login = async({ email, password }) => {
    const auth = getAuth();

    try {
        const userCredentials = await signInWithEmailAndPassword(auth, email, password);
        return userCredentials.user;
    } catch {
        return Promise.reject(new Error('Incorrect email or password'));
    }
};

const loginWithGoogle = async() => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    try {
        const res = await signInWithPopup(auth, provider);
        const details = getAdditionalUserInfo(res);
        const isNewUser = details.isNewUser;
        const user = res.user;
        const userProfile = getUserDefaultProfile(user.uid, user.displayName, user.email);

        if (isNewUser) {
            await createUserProfile(userProfile);
        }

        return user;
    } catch (error) {
        return Promise.reject(error.message);
    }
};

const loginWithFacebook = async() => {
    const auth = getAuth();
    const provider = new FacebookAuthProvider();

    try {
        const res = await signInWithPopup(auth, provider);
        const details = getAdditionalUserInfo(res);
        const isNewUser = details.isNewUser;
        const user = res.user;
        const userProfile = getUserDefaultProfile(user.uid, user.displayName, user.email);

        if (isNewUser) {
            await createUserProfile(userProfile);
        }

        return user;
    } catch (error) {
        return Promise.reject(error.message);
    }
};

const logout = async() => {
    const auth = getAuth();

    try {
        await signOut(auth);
    } catch (error) {
        return Promise.reject(error.message);
    }
};

const deleteUserAccount = async(email = '', password = '') => {
    const auth = getAuth();
    const user = auth.currentUser;

    let result;

    try {
        if (email && password) {
            const credential = EmailAuthProvider.credential(email, password);
            result = await reauthenticateWithCredential(user, credential);
        } else {
            const provider = new GoogleAuthProvider();
            result = await reauthenticateWithPopup(user, provider);
        }

        if (result) {
            await deleteUser(user);
            await deleteDoc(doc(db, 'profiles', user.uid));
        }
    } catch (error) {
        return Promise.reject(error);
    }
};

export {
    register,
    registerWithGoogle,
    login,
    loginWithGoogle,
    logout,
    deleteUserAccount,
    resetPassword,
    registerWithFacebook,
    loginWithFacebook,
};
